import * as XLSX from 'xlsx-js-style';
import { ticketService } from '../api/ticketService';
import { locationService } from '../api/locationService';

export const MAIN_SHEET_NAME = 'Bulk_Ticket_Import';

const TEMPLATE_HEADERS = [
  'requesterEmail',
  'subject',
  'description',
  'inquiryTypeId',
  'inquiryCategoryId',
  'inquirySubCategoryId',
  'phoneNumber',
  'channel',
  'countyId',
  'subCountyId',
];

function normalizeHeader(header) {
  return String(header || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '');
}

function unwrapList(response) {
  if (Array.isArray(response)) return response;
  if (response?.data?.data?.data && Array.isArray(response.data.data.data)) {
    return response.data.data.data;
  }
  if (response?.data?.data && Array.isArray(response.data.data)) {
    return response.data.data;
  }
  if (response?.data && Array.isArray(response.data)) {
    return response.data;
  }
  return [];
}

function findKenyaCountryId(countries) {
  const match = (countries || []).find(
    (c) => String(c.name || c.countryName || '').trim().toLowerCase() === 'kenya',
  );
  const id = match?.id ?? match?.countryId;
  return id != null ? Number(id) : null;
}

function mapRowToImportRow(row) {
  if (!row || typeof row !== 'object') return null;

  const normalized = {};
  Object.entries(row).forEach(([key, value]) => {
    normalized[normalizeHeader(key)] = value != null ? String(value).trim() : '';
  });

  const requesterEmail =
    normalized.requesteremail ||
    normalized.email ||
    '';
  const subject = normalized.subject || '';
  const description = normalized.description || '';

  if (!requesterEmail || !subject || !description) {
    return null;
  }

  const parseLong = (v) => {
    if (v === '' || v == null) return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };

  const parseIntField = (v) => {
    if (v === '' || v == null) return null;
    const n = parseInt(v, 10);
    return Number.isFinite(n) ? n : null;
  };

  return {
    requesterEmail: requesterEmail.toLowerCase(),
    subject,
    description,
    inquiryTypeId: parseLong(normalized.inquirytypeid),
    inquiryCategoryId: parseLong(normalized.inquirycategoryid),
    inquirySubCategoryId: parseLong(normalized.inquirysubcategoryid),
    phoneNumber: normalized.phonenumber || normalized.phone || null,
    channel: normalized.channel || null,
    countryId: parseIntField(normalized.countryid),
    countyId: parseIntField(normalized.countyid),
    subCountyId: parseIntField(normalized.subcountyid),
    // Legacy files may include wardId; parsed for compatibility but not used by D6 import.
    wardId: parseIntField(normalized.wardid),
  };
}

function sheetFromTable(headers, rows) {
  return XLSX.utils.aoa_to_sheet([headers, ...rows]);
}

function nameById(list, id) {
  if (id == null) return '';
  const item = (list || []).find((x) => Number(x.id) === Number(id));
  return item?.name || item?.inquiryTypeName || item?.inquiryCategoryName
    || item?.inquirySubCategoryName || '';
}

export async function loadBulkTicketImportReferenceData() {
  const types = await ticketService.getInquiryTypesForCreate();

  const categories = [];
  for (const type of types) {
    const typeId = type.id;
    const cats = await ticketService.getInquiryCategoriesForCreate(typeId);
    for (const cat of cats) {
      categories.push({
        id: cat.id,
        name: cat.inquiryCategoryName,
        inquiryTypeId: typeId,
        inquiryTypeName: type.inquiryTypeName,
      });
    }
  }

  const subCategories = [];
  for (const cat of categories) {
    const subs = await ticketService.getInquirySubCategoriesForCreate(cat.id);
    for (const sub of subs) {
      subCategories.push({
        id: sub.id,
        name: sub.inquirySubCategoryName,
        inquiryCategoryId: cat.id,
        inquiryCategoryName: cat.name,
      });
    }
  }

  const countries = unwrapList(await locationService.getActiveCountries());
  const kenyaCountryId = findKenyaCountryId(countries);
  if (kenyaCountryId == null) {
    throw new Error('Kenya is not available in location master data. Cannot build import template.');
  }

  const counties = unwrapList(await locationService.getActiveCounties(kenyaCountryId));
  const subCounties = unwrapList(await locationService.getActiveSubCounties());

  const countyIds = new Set(counties.map((c) => Number(c.id)));
  const subCountiesInKenya = subCounties.filter((sc) => countyIds.has(Number(sc.parentId)));

  return {
    inquiryTypes: types.map((t) => [t.id, t.inquiryTypeName]),
    categories: categories.map((c) => [c.id, c.name, c.inquiryTypeId, c.inquiryTypeName]),
    subCategories: subCategories.map((s) => [s.id, s.name, s.inquiryCategoryId, s.inquiryCategoryName]),
    counties: counties.map((c) => [c.id, c.name]),
    subCounties: subCountiesInKenya.map((sc) => {
      const countyName = nameById(counties, sc.parentId);
      return [sc.id, sc.name, sc.parentId, countyName];
    }),
  };
}

export function buildBulkTicketImportTemplateWorkbook(referenceData) {
  const wb = XLSX.utils.book_new();

  const main = sheetFromTable(TEMPLATE_HEADERS, []);
  XLSX.utils.book_append_sheet(wb, main, MAIN_SHEET_NAME);

  if (referenceData) {
    XLSX.utils.book_append_sheet(
      wb,
      sheetFromTable(['ID', 'Name'], referenceData.inquiryTypes || []),
      'Inquiry_Types',
    );
    XLSX.utils.book_append_sheet(
      wb,
      sheetFromTable(['ID', 'Name', 'Inquiry Type ID', 'Inquiry Type Name'], referenceData.categories || []),
      'Categories',
    );
    XLSX.utils.book_append_sheet(
      wb,
      sheetFromTable(['ID', 'Name', 'Category ID', 'Category Name'], referenceData.subCategories || []),
      'Sub_Categories',
    );
    XLSX.utils.book_append_sheet(
      wb,
      sheetFromTable(['ID', 'Name'], referenceData.counties || []),
      'Counties',
    );
    XLSX.utils.book_append_sheet(
      wb,
      sheetFromTable(['ID', 'Name', 'County ID', 'County Name'], referenceData.subCounties || []),
      'Sub_Counties',
    );
  }

  return wb;
}

export async function downloadBulkTicketImportTemplate() {
  const referenceData = await loadBulkTicketImportReferenceData();
  const wb = buildBulkTicketImportTemplateWorkbook(referenceData);
  XLSX.writeFile(wb, 'bulk_ticket_import_template.xlsx');
}

export async function parseBulkTicketImportCsv(text) {
  const lines = text.split(/\r?\n/).filter((line) => line.trim().length > 0);
  if (lines.length < 2) return [];

  const headers = lines[0].split(',').map((h) => normalizeHeader(h));
  const rows = [];

  for (let i = 1; i < lines.length; i++) {
    const values = lines[i].split(',').map((v) => v.trim());
    const rowObj = {};
    headers.forEach((header, index) => {
      rowObj[header] = values[index] || '';
    });
    const mapped = mapRowToImportRow(rowObj);
    if (mapped) rows.push(mapped);
  }
  return rows;
}

function readMainImportSheet(workbook) {
  if (workbook.Sheets[MAIN_SHEET_NAME]) {
    return workbook.Sheets[MAIN_SHEET_NAME];
  }
  const first = workbook.SheetNames[0];
  return first ? workbook.Sheets[first] : null;
}

export async function parseBulkTicketImportExcel(file) {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array' });
  const sheet = readMainImportSheet(workbook);
  if (!sheet) return [];
  const sheetRows = XLSX.utils.sheet_to_json(sheet, { defval: '' });
  return sheetRows.map(mapRowToImportRow).filter(Boolean);
}

export async function parseBulkTicketImportFile(file) {
  const fileName = file.name.toLowerCase();
  if (fileName.endsWith('.csv')) {
    const text = await file.text();
    return parseBulkTicketImportCsv(text);
  }
  if (fileName.endsWith('.xlsx') || fileName.endsWith('.xls')) {
    return parseBulkTicketImportExcel(file);
  }
  throw new Error('Unsupported file type. Use CSV or Excel (.xlsx, .xls).');
}

export const BULK_IMPORT_ROW_STATUS = {
  IMPORTED: 'IMPORTED',
  VALIDATION_REJECTED: 'VALIDATION_REJECTED',
  DUPLICATE_REJECTED: 'DUPLICATE_REJECTED',
};

export function statusBadgeVariant(status) {
  switch (status) {
    case BULK_IMPORT_ROW_STATUS.IMPORTED:
      return 'success';
    case BULK_IMPORT_ROW_STATUS.DUPLICATE_REJECTED:
      return 'warning';
    default:
      return 'destructive';
  }
}
